import React from 'react';
import { ArrowDown, ArrowUp, ChevronRight } from 'lucide-react';
import type { MarginRow, PivotDimension, PivotTotals } from '../../../services/aiControlApi';
import { RubyLoader } from '../../../components/RubyLoader';

/**
 * Vendor spend pivot — what the vendors billed us, by tenant / service /
 * feature / role / model. It carries NO charge, revenue or margin columns:
 * Ruby engine spend rows record creditsCharged 0 (the charge lives on the RUN
 * row), so a "charged" figure built from spend rows reads $0. Charged and
 * margin are the Ruby Ledger's (final review #3).
 */
const usd = (n: number, dp = 2): string => `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(dp)}`;

const TH = 'px-3 py-2 text-[11px] font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap';
const TD = 'px-3 py-2 text-sm whitespace-nowrap';

interface Props {
  dimension: PivotDimension;
  rows: MarginRow[];
  totals: PivotTotals;
  loading: boolean;
  sortDir: 'asc' | 'desc';
  onSortToggle: () => void;
  onDrill: (row: MarginRow) => void;
}

const DIMENSION_HEADING: Record<PivotDimension, string> = {
  tenant: 'Tenant',
  service: 'Service',
  feature: 'Feature',
  role: 'Role',
  model: 'Model',
};

const UsagePivotTable: React.FC<Props> = ({
  dimension, rows, totals, loading, sortDir, onSortToggle, onDrill,
}) => {
  if (loading) {
    return (
      <RubyLoader variant="section" />
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-10 text-center text-sm text-gray-500">
        No AI spend recorded in this window.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-gray-900">
        <thead className="bg-stone-50 border-b border-stone-100">
          <tr>
            <th className={`${TH} text-left`}>{DIMENSION_HEADING[dimension]}</th>
            <th className={`${TH} text-right`}>Calls</th>
            <th className={`${TH} text-right hidden md:table-cell`}>Attempts</th>
            <th className={`${TH} text-right`}>Tokens</th>
            <th className={`${TH} text-right`}>
              <button
                type="button"
                onClick={onSortToggle}
                className="inline-flex items-center gap-1 min-h-[44px] text-[11px] font-medium text-gray-500 uppercase tracking-wide hover:text-gray-800"
              >
                Vendor cost
                {sortDir === 'desc' ? <ArrowDown size={11} /> : <ArrowUp size={11} />}
              </button>
            </th>
            <th className={`${TH} text-right`} />
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map((r) => (
            <tr
              key={r.key}
              className="hover:bg-stone-50 cursor-pointer"
              onClick={() => onDrill(r)}
            >
              <td className={`${TD} text-gray-900`}>
                <div className="min-w-0">
                  <p className="truncate max-w-[18rem]">{r.label}</p>
                  {r.failedCount > 0 && (
                    <span className="text-[10px] text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded">
                      {r.failedCount} failed
                    </span>
                  )}
                  {r.estimatedCostUsd > 0 && (
                    <span className="ml-1 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                      {usd(r.estimatedCostUsd, 4)} estimated
                    </span>
                  )}
                </div>
              </td>
              <td className={`${TD} text-right text-gray-800`}>{r.requests.toLocaleString()}</td>
              <td className={`${TD} text-right hidden md:table-cell ${
                r.attempts > r.requests ? 'text-amber-700 font-medium' : 'text-gray-500'
              }`}>
                {r.attempts.toLocaleString()}
              </td>
              <td className={`${TD} text-right text-gray-800`}>{r.tokens.toLocaleString()}</td>
              <td className={`${TD} text-right font-semibold text-gray-900`}>{usd(r.costUsd, 4)}</td>
              <td className={`${TD} text-right`}>
                <ChevronRight size={14} className="text-gray-300 inline" />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-stone-50 border-t-2 border-stone-200">
          <tr>
            <td className={`${TD} font-semibold text-gray-900`}>Total</td>
            <td className={`${TD} text-right font-semibold text-gray-900`}>{totals.requests.toLocaleString()}</td>
            <td className={`${TD} text-right font-semibold text-gray-900 hidden md:table-cell`}>{totals.attempts.toLocaleString()}</td>
            <td className={`${TD} text-right font-semibold text-gray-900`}>{totals.tokens.toLocaleString()}</td>
            <td className={`${TD} text-right font-bold text-gray-900`}>{usd(totals.costUsd, 4)}</td>
            <td className={TD} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default UsagePivotTable;
